import { useState, useMemo } from 'react';
import { LayoutGrid, List, Users, CheckCircle, XCircle, Clock, FileText } from 'lucide-react';
import PageHeader from '../components/layout/PageHeader';
import RegistrationFilters from '../components/registrations/RegistrationFilters';
import RegistrationCard from '../components/registrations/RegistrationCard';
import RegistrationTable from '../components/registrations/RegistrationTable';
import ExportButton from '../components/shared/ExportButton';
import Pagination from '../components/shared/Pagination';
import { useRegistrations } from '../hooks/useRegistrations';
import { useDebounce } from '../hooks/useDebounce';

const statItems = [
  { key: 'total', label: 'Total', icon: Users, color: 'bg-ysrcp-50 text-ysrcp-600', iconColor: 'text-ysrcp-500' },
  { key: 'SUBMITTED', label: 'Submitted', icon: FileText, color: 'bg-blue-50 text-blue-700', iconColor: 'text-blue-500' },
  { key: 'APPROVED', label: 'Approved', icon: CheckCircle, color: 'bg-emerald-50 text-emerald-700', iconColor: 'text-emerald-500' },
  { key: 'REJECTED', label: 'Rejected', icon: XCircle, color: 'bg-red-50 text-red-700', iconColor: 'text-red-500' },
  { key: 'PENDING', label: 'Pending', icon: Clock, color: 'bg-amber-50 text-amber-700', iconColor: 'text-amber-500' },
];

export default function Registrations() {
  const [filters, setFilters] = useState({
    search: '', status: '', assemblyId: '', committeeType: '', level: '',
    mandal: '', panchayat: '', casteCategory: '', gender: '', wing: '',
    withoutPhoto: '', fromDate: '', toDate: '',
    page: 1, limit: 50, sortBy: 'created_date', sortOrder: 'desc',
  });
  const [view, setView] = useState('card');

  const debouncedSearch = useDebounce(filters.search);
  const queryFilters = useMemo(() => ({ ...filters, search: debouncedSearch }), [filters, debouncedSearch]);
  const { data, total, totalPages, loading, statusCounts } = useRegistrations(queryFilters);

  const getStatCount = (key) => {
    if (key === 'total') return total;
    return statusCounts?.[key] || 0;
  };

  return (
    <div className="space-y-5">
      <PageHeader title="Registrations" subtitle={`${total.toLocaleString()} total records`}>
        <ExportButton filters={queryFilters} />
      </PageHeader>

      {/* Stats Bar */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {statItems.map(({ key, label, icon: Icon, color, iconColor }) => (
          <button
            key={key}
            onClick={() => key !== 'total' ? setFilters(f => ({ ...f, status: f.status === key ? '' : key, page: 1 })) : setFilters(f => ({ ...f, status: '', page: 1 }))}
            className={`relative flex items-center gap-3 px-4 py-3 rounded-xl border transition-all duration-200 ${
              (key === 'total' && !filters.status) || filters.status === key
                ? 'border-ysrcp-500 ring-2 ring-ysrcp-100 shadow-sm'
                : 'border-gray-100 hover:border-gray-200 hover:shadow-sm'
            } bg-white`}
          >
            <div className={`w-10 h-10 rounded-lg ${color} flex items-center justify-center flex-shrink-0`}>
              <Icon className={`w-5 h-5 ${iconColor}`} />
            </div>
            <div className="text-left min-w-0">
              <p className="text-xs text-gray-500 font-medium">{label}</p>
              <p className="text-lg font-bold text-gray-900 leading-tight">{getStatCount(key).toLocaleString()}</p>
            </div>
          </button>
        ))}
      </div>

      <RegistrationFilters filters={filters} onChange={setFilters} />

      {/* Toolbar */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">
          Showing <span className="font-medium text-gray-700">{data.length}</span> of <span className="font-medium text-gray-700">{total.toLocaleString()}</span>
        </p>
        <div className="flex items-center gap-1 bg-white rounded-lg border border-gray-200 p-0.5 shadow-sm">
          <button
            onClick={() => setView('card')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-200 ${
              view === 'card'
                ? 'bg-ysrcp-500 text-white shadow-sm'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}
          >
            <LayoutGrid className="w-4 h-4" />
            <span className="hidden sm:inline">Cards</span>
          </button>
          <button
            onClick={() => setView('table')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-200 ${
              view === 'table'
                ? 'bg-ysrcp-500 text-white shadow-sm'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}
          >
            <List className="w-4 h-4" />
            <span className="hidden sm:inline">Table</span>
          </button>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex flex-col items-center justify-center h-48 gap-3">
          <div className="w-12 h-12 rounded-full border-4 border-ysrcp-100 border-t-ysrcp-500 animate-spin" />
          <p className="text-sm text-gray-400">Loading registrations...</p>
        </div>
      ) : data.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 gap-3">
          <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center">
            <Users className="w-8 h-8 text-gray-300" />
          </div>
          <p className="text-gray-400 font-medium">No registrations found</p>
          <p className="text-sm text-gray-300">Try adjusting your filters or search query</p>
        </div>
      ) : view === 'card' ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {data.map(reg => <RegistrationCard key={reg.id} reg={reg} />)}
        </div>
      ) : (
        <RegistrationTable
          data={data}
          sortBy={filters.sortBy}
          sortOrder={filters.sortOrder}
          onSort={(col, order) => setFilters(f => ({ ...f, sortBy: col, sortOrder: order }))}
        />
      )}

      <Pagination
        page={filters.page}
        totalPages={totalPages}
        total={total}
        limit={filters.limit}
        onPageChange={(p) => setFilters(f => ({ ...f, page: p }))}
        onLimitChange={(l) => setFilters(f => ({ ...f, limit: l, page: 1 }))}
      />
    </div>
  );
}
